import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';

import { MatDatepicker } from '@angular/material/datepicker';

import * as _moment from 'moment';
import { Moment } from 'moment';
import { OperationsDto, OperationsQueryDto } from '@app/requests/model/requests-model';
import { TranslateService } from '@ngx-translate/core';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { DatepickerUtilsService } from '@app/@shared/services/utils/datepicker-utils.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LOCALE_ES } from '@app/@core/constants/common.constants';
import { UsersApiService } from '@app/users/users-services/users-api.service';
import { SearchOperationDto } from '@app/users/model/users-model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { Router } from '@angular/router';
import { SearchStore } from '@app/users/operation-search/search-store';
import { PanelStore } from '@app/requests/panel/panel.store';

const moment = _moment;

@UntilDestroy()
@Component({
  selector: 'scf-search-panel',
  templateUrl: './search-panel.component.html',
  styleUrls: ['./search-panel.component.scss'],
})
export class SearchPanelComponent implements OnInit, OnChanges {
  @Input() public currentState: OperationsQueryDto;
  @Output() public changeOnSearchEvent = new EventEmitter<any>();
  @Output() public changeSearchView = new EventEmitter<any>();

  public searchDataForm: FormGroup;

  public isRequestButtonDisabled: boolean = true;
  public isDatepickerDisabled: boolean = true;

  public statusRequests: CommonDataDTO[];
  public statusRequestsId: CommonDataDTO[];
  public currentDate: Date = new Date();
  public subtitle: string;

  public minDate: Date = new Date('1/1/1900');

  searchButtonDisabled: boolean = true;
  operationsByStateList: OperationsDto[];
  operationFound: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private customValidators: CustomValidatorsService,
    private combosStore: CombosStore,
    private readonly searchStore: SearchStore,
    private readonly panelStore: PanelStore,
    private translateService: TranslateService,
    private readonly datepickerUtils: DatepickerUtilsService,
    private readonly userService: UsersApiService,
    private readonly dialog: MatDialog,
    public router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.currentState.isFirstChange()) {
      this.searchDataForm.controls.state.setValue(this.currentState.state);
      this.searchDataForm.controls.state.updateValueAndValidity({ onlySelf: true });

      this.searchDataForm.controls.initialDate.setValue(this.currentState.initialDate);
      this.searchDataForm.controls.finalDate.setValue(this.currentState.finalDate);

      this.currentState.state === 'TS'
        ? (this.subtitle = this.translateService.instant('request.panel.subtitle.label.lastFive'))
        : (this.subtitle = this.translateService.instant('request.panel.subtitle.label.all'));
    }
  }

  ngOnInit(): void {
    this.subtitle = this.translateService.instant('request.panel.subtitle.label.lastFive');

    this.translateService.onLangChange.subscribe((event) => {
      this.subtitle = this.translateService.instant('request.panel.subtitle.label.lastFive');
    });

    this.searchDataForm = this.formBuilder.group(
      {
        state: [this.currentState?.state ? this.currentState.state : 'TS', Validators.required],
        initialDate: [{ value: '', disabled: true }, [this.customValidators.checkIsFutureDate]],
        finalDate: [{ value: '', disabled: true }, [this.customValidators.checkIsFutureDate]],
        operationNumber: ['', [this.customValidators.isOperationValid]],
        nifNie: ['', [this.customValidators.isNifOrCifValid]],
      },
      { validators: [this.customValidators.isDateValid('initialDate', 'finalDate', 'dateInvalid')] }
    );

    //Load state combo
    this.combosStore.statusRequestsList$.subscribe(
      (statusRequestsList) => {
        this.statusRequests = [{ id: 'TS', value: 'request.search.status.all' }, ...statusRequestsList];
        this.statusRequestsId = JSON.parse(JSON.stringify(this.statusRequests));
        this.statusRequests.forEach((item) => {
          item.value = this.translateService.instant(item.value);
        });
      },
      (error) => console.error(error)
    );

    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.statusRequests.forEach((item) => {
        item.value = this.translateService.instant(
          this.statusRequestsId.find((valueListIds) => item.id == valueListIds.id).value
        );
      });
    });

    //Oberserver to enable or disable date inputs and button
    this.searchDataForm.controls.state.valueChanges.subscribe((value) => {
      const initialDate = this.searchDataForm.controls.initialDate;
      const finalDate = this.searchDataForm.controls.finalDate;
      if (value === 'TS') {
        this.searchDataForm.controls.initialDate.reset();
        this.searchDataForm.controls.finalDate.reset();
        initialDate.disable();
        finalDate.disable();
        value === 'TS' && this.currentState.state !== 'TS'
          ? (this.isRequestButtonDisabled = false)
          : (this.isRequestButtonDisabled = true);
      } else {
        //init dates
        this.searchDataForm.controls.initialDate.setValue(this.currentState.initialDate);
        this.searchDataForm.controls.finalDate.setValue(this.currentState.finalDate);

        initialDate.enable();
        finalDate.enable();
      }
    });

    this.translateService.currentLang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan();
    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe((event) =>
        event.lang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan()
      );
  }

  stateSearchPanelChange() {
    console.log('stateSearchPanelChange', this.searchDataForm.controls.state?.value);
    if (this.searchDataForm.controls.state?.value === 'TS') {
      this.searchDataForm.controls.initialDate.setValue('');
      this.searchDataForm.controls.finalDate.setValue('');
    }
  }

  onSearchClick() {
    console.log('State: ', this.searchDataForm.controls.state?.value);
    let searchParams: OperationsQueryDto = {
      state: this.statusRequests.find((status) => status.id === this.searchDataForm.controls.state?.value).id,
      initialDate: this.searchDataForm.controls.initialDate?.value,
      finalDate: this.searchDataForm.controls.finalDate?.value,
    };

    if (this.searchDataForm.controls.operationNumber?.value || this.searchDataForm.controls.nifNie?.value) {
      this.searchOperation();
      this.changeSearchView.emit(true);
    } else if (searchParams?.state) {
      this.operationsByStateList = [];
      this.changeOnSearchEvent.emit(searchParams);
    }

    this.currentState = searchParams;
  }

  /* DATEPICKER HANDLERS */
  pickerDatesHandler(date: Moment, input: any, datepicker?: MatDatepicker<Moment>) {
    const inputForm = typeof input === 'string' ? input : input?.getAttribute('formControlName');
    const control: AbstractControl = this.searchDataForm.controls[inputForm];

    control.setValue(moment('1/' + (date.month() + 1).toString() + '/' + date.year().toString(), 'DD-MM-YYYY'));

    if (datepicker) datepicker.close();
  }

  numberOnly(event: any, element: any): boolean {
    const inputForm = typeof element === 'string' ? element : element?.getAttribute('formControlName');
    const value = this.searchDataForm.controls[inputForm].value;

    let returnBoolean = this.customValidators.numberOnly(event.which ? event.which : event.keyCode, value);
    if (value.length >= 15) {
      returnBoolean = false;
    }

    return returnBoolean;
  }

  onKeyUp() {
    if (
      this.searchDataForm.controls['operationNumber'].value?.length > 0 ||
      this.searchDataForm.controls['nifNie'].value?.length > 0
    ) {
      this.searchDataForm.controls['state'].disable();
      this.searchDataForm.controls['state'].setValue('TS');
    } else {
      this.searchDataForm.controls['state'].enable();
    }
  }

  searchOperation() {
    let numOperation = this.searchDataForm.controls.operationNumber.value;
    let nifNie = this.searchDataForm.controls.nifNie.value;
    if (!numOperation) {
      numOperation = '';
    }
    if (!nifNie) {
      nifNie = '';
    }

    this.callSearchService({ operation: numOperation, nif_nie: nifNie });
  }

  callSearchService(criteria: SearchOperationDto) {
    new Promise<void>((res, rej) => {
      this.userService
        .postSearchOperation(criteria)
        .toPromise()
        .then(
          (result: OperationsDto[]) => {
            this.operationsByStateList = [];
            if (result && result.length > 0) {
              this.operationsByStateList = result;
              this.operationFound = true;
            } else {
              console.log(result);
              this.showDialogError(this.translateService.instant('dialog.error.no.data.found'));
            }
            res();
          },
          (error) => {
            //mostrar dialog de error
            if (!localStorage.getItem('previusRoute').includes('/panel')) {
              this.showDialogError(error.error.message);
            }
          }
        );
    });
    const url = this.router.url.split(';');
    setTimeout(() => localStorage.setItem('pageOpenSignature', JSON.stringify({ url: url[0] })), 2000);
  }

  private showDialogError(error: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.maxWidth = '450px';
    dialogConfig.data = { text: error, icon: 'icon-ic_circle_cross' };
    this.dialog.open(ResponseDialogComponent, dialogConfig);
  }

  keepFilterSearch() {
    console.log('keep filter');
    this.searchStore.setSearchCriteria({
      operation: this.searchDataForm.controls.operationNumber.value,
      nif_nie: this.searchDataForm.controls.nifNie.value,
    });
  }

  setState(status: string): CommonDataDTO {
    return this.panelStore.getStatusByValue(status);
  }
}
